/* Search and browse the catalogue: our own database first, with Open Food Facts'
   live search topping up a short list. A result opens the analyse screen (by
   barcode) or loads into the compare screen. */

import { fetchFacets, searchProducts, suggestNames, describeFailure } from "./api";
import { escapeHtml, html, qs, toast } from "./dom";
import { novaColour, packArt, sigaColour } from "./grade";
import { icon } from "./icons";
import type { Facets, SearchItem, SearchQuery } from "./types";
import { compareWith, go, pageHead, wireBack } from "./views";

const DEBOUNCE_MS = 300;
const GRADES = ["A", "B", "C", "D", "E"];

function tagLabel(tag: string): string {
  const name = tag.split(":").pop() ?? tag;
  return name.replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase());
}

function badges(item: SearchItem): string {
  const out: string[] = [];
  if (item.nova_group) {
    out.push(`<span class="mini-badge" style="--c:${novaColour(item.nova_group)}"><small>NOVA</small>${item.nova_group}</span>`);
  }
  if (item.siga_class) {
    out.push(`<span class="mini-badge" style="--c:${sigaColour(item.siga_class)}"><small>SIGA</small>${escapeHtml(item.siga_class)}</span>`);
  }
  if (item.grade) out.push(`<span class="mini-badge grade-${item.grade.toLowerCase()}"><small>GRADE</small>${item.grade}</span>`);
  if (!out.length) out.push(`<span class="muted small">Not graded yet. Open it to analyse.</span>`);
  return out.join("");
}

function sourceNote(item: SearchItem): string {
  if (item.source === "scan") return `<span class="tag">From a user's photos, unverified</span>`;
  if (item.source === "live") return `<span class="tag">From Open Food Facts</span>`;
  if (item.has_ingredients === false) return `<span class="tag">No ingredient list</span>`;
  return "";
}

function card(item: SearchItem): string {
  const thumb = item.image_front_url
    ? `<img src="${escapeHtml(item.image_front_url)}" alt="" loading="lazy">`
    : packArt(item.name, item.brand, item.barcode);
  const sub = [item.brand, item.quantity].filter(Boolean).join(" · ");
  return `<article class="result-card card" data-code="${escapeHtml(item.barcode)}">
    <div class="result-thumb">${thumb}</div>
    <div class="result-body">
      <strong>${escapeHtml(item.name)}</strong>
      <small class="muted">${escapeHtml(sub)}${sub ? " · " : ""}EAN ${escapeHtml(item.barcode)}</small>
      <div class="result-badges">${badges(item)}</div>
      ${sourceNote(item)}
      <div class="result-actions">
        <button class="btn soft small act-analyse" type="button">${icon("analyse")}<span>Analyse</span></button>
        <button class="btn soft small act-compare" type="button">${icon("compare")}<span>Compare</span></button>
      </div>
    </div>
  </article>`;
}

export function searchView(main: HTMLElement, params: URLSearchParams): void {
  const state: SearchQuery = {
    q: params.get("q") ?? "", category: "", brand: "", nova: "", grade: "", sort: "", page: 1,
  };
  let facets: Facets | null = null;
  let items: SearchItem[] = [];
  let total = 0;
  let controller: AbortController | null = null;
  let timer = 0;

  main.innerHTML = `${pageHead("Search products")}
    <p class="lede">Find a product by name or browse by brand and category.</p>
    <div class="field search-field">
      ${icon("search")}
      <input class="s-q" type="search" list="s-suggest" autocomplete="off" placeholder="e.g. chaat masala, Maggi, biscuit"
             aria-label="Search products" value="${escapeHtml(state.q)}">
      <datalist id="s-suggest"></datalist>
    </div>
    <div class="filters">
      <div class="chips" role="group" aria-label="Grade">
        <span class="chips-label">Grade</span>
        ${GRADES.map((g) => `<button class="chip" type="button" data-grade="${g}" aria-pressed="false">${g}</button>`).join("")}
      </div>
      <div class="chips" role="group" aria-label="NOVA group">
        <span class="chips-label">NOVA</span>
        ${[1, 2, 3, 4].map((n) => `<button class="chip" type="button" data-nova="${n}" aria-pressed="false">${n}</button>`).join("")}
      </div>
      <div class="selects">
        <select class="s-category" aria-label="Category"><option value="">All categories</option></select>
        <select class="s-brand" aria-label="Brand"><option value="">All brands</option></select>
        <select class="s-sort" aria-label="Sort by">
          <option value="">Best match</option>
          <option value="popular">Most popular</option>
          <option value="grade">Best grade</option>
          <option value="nova">Least processed</option>
        </select>
      </div>
    </div>
    <p class="muted small s-count" role="status"></p>
    <div class="results"></div>
    <div class="s-more" hidden><button class="btn soft wide" type="button">Show more</button></div>
    <p class="muted small attribution">Product data from Open Food Facts, available under the Open Database License.</p>`;
  wireBack(main);

  const box = qs<HTMLInputElement>(main, ".s-q");
  const list = qs(main, ".results");
  const count = qs(main, ".s-count");
  const more = qs(main, ".s-more");

  const draw = (append: boolean, from: number): void => {
    const shown = items.slice(from);
    if (!append) list.innerHTML = "";
    for (const item of shown) {
      const node = html(card(item));
      qs(node, ".act-analyse").addEventListener("click", () => go("/analyse?barcode=" + item.barcode));
      qs(node, ".act-compare").addEventListener("click", () =>
        compareWith({ barcode: item.barcode, name: item.name }));
      list.appendChild(node);
    }
    more.hidden = items.length >= total;
  };

  const load = async (append: boolean): Promise<void> => {
    controller?.abort();
    controller = new AbortController();
    const from = append ? items.length : 0;
    count.textContent = "Searching…";
    try {
      const result = await searchProducts(state, controller.signal);
      items = append ? items.concat(result.items) : result.items;
      total = result.total;
      draw(append, from);
      count.textContent = items.length
        ? `${total.toLocaleString()} product${total === 1 ? "" : "s"}${result.live ? " (some from Open Food Facts)" : ""}`
        : "";
      if (!items.length) {
        list.innerHTML = `<div class="empty card"><strong>No products found</strong>
          <p class="muted">Try fewer words or clear a filter. You can also scan the barcode or snap the label.</p>
          <a class="btn soft" href="#/analyse">${icon("analyse")}<span>Analyse a product</span></a></div>`;
      }
      if (result.database === false && !items.length) count.textContent = "The catalogue is unavailable right now.";
    } catch (error) {
      if ((error as Error).name === "AbortError") return;
      count.textContent = "";
      toast(describeFailure(error), "error");
    }
  };

  const reload = (): void => {
    state.page = 1;
    void load(false);
  };
  const later = (): void => {
    window.clearTimeout(timer);
    timer = window.setTimeout(reload, DEBOUNCE_MS);
  };

  box.addEventListener("input", () => {
    state.q = box.value.trim();
    later();
    // Name suggestions while typing (a datalist; the browser filters it).
    if (state.q.length >= 2) {
      void suggestNames(state.q).then((names) => {
        qs(main, "#s-suggest").innerHTML = names.map((n) => `<option value="${escapeHtml(n)}">`).join("");
      }).catch(() => undefined);
    }
  });

  const toggle = (attr: "grade" | "nova"): void => {
    main.querySelectorAll<HTMLButtonElement>(`[data-${attr}]`).forEach((b) => {
      b.addEventListener("click", () => {
        const value = b.dataset[attr] ?? "";
        state[attr] = state[attr] === value ? "" : value;
        main.querySelectorAll<HTMLButtonElement>(`[data-${attr}]`).forEach((o) =>
          o.setAttribute("aria-pressed", String(o.dataset[attr] === state[attr])));
        reload();
      });
    });
  };
  toggle("grade");
  toggle("nova");

  for (const [selector, key] of [[".s-category", "category"], [".s-brand", "brand"], [".s-sort", "sort"]] as const) {
    qs<HTMLSelectElement>(main, selector).addEventListener("change", (event) => {
      state[key] = (event.target as HTMLSelectElement).value;
      reload();
    });
  }
  qs(more, "button").addEventListener("click", () => {
    state.page += 1;
    void load(true);
  });

  void fetchFacets().then((f) => {
    facets = f;
    const fill = (selector: string, entries: Facets["brands"]): void => {
      qs<HTMLSelectElement>(main, selector).insertAdjacentHTML(
        "beforeend",
        entries.map((e) => `<option value="${escapeHtml(e.tag)}">${escapeHtml(tagLabel(e.tag))} (${e.count})</option>`).join(""));
    };
    fill(".s-category", facets.categories);
    fill(".s-brand", facets.brands);
  }).catch(() => undefined);

  void load(false);
}
